/**
 * AuthContext - Firebase Authentication Context
 *
 * Provides authentication state and methods using Firebase Auth SDK directly.
 * Auto-creates editor documents for new Firebase Auth users.
 *
 * Design Patterns:
 * - Provider Pattern: Global auth state management
 * - Observer Pattern: Listens to Firebase auth state changes
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { COLLECTIONS } from '../constants';
import type { Editor, EditorRole } from '../types';

// Auth state interface
export interface AuthState {
  user: User | null;
  editor: Editor | null;
  loading: boolean;
  error: string | null;
}

// Auth context interface
export interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isAdmin: boolean;
  isEditor: boolean;
  isAuthenticated: boolean;
}

// Create context
const AuthContext = createContext<AuthContextValue | null>(null);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    editor: null,
    loading: true,
    error: null,
  });

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch or create editor profile from Firestore
          const editorRef = doc(db, COLLECTIONS.editors, user.uid);
          const editorSnap = await getDoc(editorRef);

          let editorData: Omit<Editor, 'id'>;

          if (editorSnap.exists()) {
            editorData = editorSnap.data() as Omit<Editor, 'id'>;

            // Update last login timestamp
            await updateDoc(editorRef, {
              lastLoginAt: serverTimestamp(),
            });
          } else {
            // Auto-create editor document for Firebase Auth users
            // First user becomes admin, subsequent users become editor
            const editorsSnapshot = await getDocs(
              query(collection(db, COLLECTIONS.editors), limit(1))
            );
            const isFirstUser = editorsSnapshot.empty;
            const role: EditorRole = isFirstUser ? 'admin' : 'editor';
            const displayName = user.displayName || user.email?.split('@')[0] || 'User';

            editorData = {
              email: user.email || '',
              displayName,
              role,
              createdAt: new Date(),
              lastLoginAt: new Date(),
            };

            await setDoc(editorRef, {
              ...editorData,
              createdAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            });

            console.log(`Auto-created editor: ${user.email} with role: ${role}`);
          }

          setState({
            user,
            editor: { id: user.uid, ...editorData },
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Error fetching/creating editor profile:', error);
          setState({
            user: null,
            editor: null,
            loading: false,
            error: '사용자 정보를 불러오는데 실패했습니다.',
          });
        }
      } else {
        setState({
          user: null,
          editor: null,
          loading: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change listener will handle the rest
    } catch (error: any) {
      let errorMessage = '로그인에 실패했습니다.';

      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = '올바른 이메일 형식이 아닙니다.';
          break;
        case 'auth/user-disabled':
          errorMessage = '비활성화된 계정입니다.';
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
          break;
        case 'auth/too-many-requests':
          errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도하세요.';
          break;
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      throw new Error(errorMessage);
    }
  };

  // Sign out
  const signOut = async () => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: '로그아웃에 실패했습니다.',
      }));
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      let errorMessage = '비밀번호 재설정 이메일 전송에 실패했습니다.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = '등록되지 않은 이메일입니다.';
      }

      throw new Error(errorMessage);
    }
  };

  // Computed values
  const isAuthenticated = !!state.user && !!state.editor;
  const isAdmin = state.editor?.role === 'admin';
  const isEditor = state.editor?.role === 'editor' || isAdmin;

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    resetPassword,
    isAdmin,
    isEditor,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// Hook to require authentication (redirects to login if not authenticated)
export function useRequireAuth(requiredRole?: EditorRole) {
  const auth = useAuth();

  const isAuthorized =
    auth.isAuthenticated &&
    (!requiredRole ||
      requiredRole === 'editor' ||
      (requiredRole === 'admin' && auth.isAdmin));

  return {
    ...auth,
    isAuthorized,
  };
}
